import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Chip,
  Stack,
  CircularProgress,
  Button,
  InputBase,
  Paper,
  Container,
  Divider
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Opinion } from '../../interface/Opinion';
import { getOpinions, voteOpinion } from '../../services/OpinionService';
import OpinionCard from './OpinionCard';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../auth/LoginModal';
import MainLayout from '../layout/MainLayout';
import UserAvatar from '../common/UserAvatar';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const currentUserId = user?._id;
  const currentUsername = user?.username;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [opinions, setOpinions] = useState<Opinion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [voting, setVoting] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'following' | 'trending' | 'recent'>('all');
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const observerTarget = useRef<HTMLDivElement>(null);

  // Reset and load initial opinions when filter changes
  useEffect(() => {
    setOpinions([]);
    setPage(1);
    setHasMore(true);
    loadOpinions(1, true);
  }, [filter]);

  // Load opinions function
  const loadOpinions = async (pageNum: number, isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const feedType = filter === 'following' ? 'following' : 'all';
      const response = await getOpinions(pageNum, 12, feedType);
      
      if (isInitial) {
        setOpinions(response.opinions);
      } else {
        setOpinions(prev => [...prev, ...response.opinions]);
      }
      
      setHasMore(response.opinions.length === 12 && pageNum < response.pagination.totalPages);
    } catch (error) {
      console.error('Error loading opinions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more opinions
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOpinions(nextPage, false);
    }
  }, [page, loadingMore, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loading, loadMore]);

  const handleVote = async (opinionId: string, voteType: 'agree' | 'disagree') => {
    if (!currentUserId) {
      setLoginModalOpen(true);
      return;
    }

    try {
      setVoting(opinionId);
      const response = await voteOpinion(opinionId, {
        userId: currentUserId,
        voteType
      });

      setOpinions(prev => prev.map(opinion =>
        opinion._id === opinionId
          ? { ...opinion, ...response.opinion }
          : opinion
      ));
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setAnchorEl(null);
  };


  const filteredOpinions = opinions.filter(opinion =>
    opinion.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    opinion.username.toLowerCase().includes(searchQuery.toLowerCase())
  );


  return (
    <MainLayout>
      <Helmet>
        <title>Active Opinions | OpinLoop</title>
        <meta name="description" content="Browse and engage with active opinions on OpinLoop. Share your thoughts, vote, and join discussions with like-minded people." />
        <meta name="keywords" content="opinions, active discussions, community, feedback, voting, engage" />
        <meta property="og:title" content="Active Opinions | OpinLoop" />
        <meta property="og:description" content="Browse and engage with active opinions on OpinLoop. Share your thoughts, vote, and join discussions." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://opinloop.com/home" />
      </Helmet>
      
      <Box>
        {/* App Bar */}
        <AppBar
          position="sticky"
          elevation={1}
          sx={{
            bgcolor: 'background.paper',
            zIndex: theme.zIndex.drawer + 1
          }}
        >
          <Toolbar sx={{ minHeight: { xs: '56px !important', sm: '64px !important' }, px: { xs: 1.5, sm: 2, md: 3 }, gap: { xs: 1, sm: 1.5 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            {/* Search Bar */}
            <Paper
              component="form"
              elevation={0}
              sx={{
                p: { xs: '4px 8px', sm: '6px 12px' },
                display: 'flex',
                alignItems: 'center',
                flex: { xs: 1, sm: 'none' },
                width: { xs: '100%', sm: 280, md: 360 },
                maxWidth: { xs: '100%', sm: 360 },
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                order: { xs: 3, sm: 0 },
                mt: { xs: 1, sm: 0 }
              }}
            >
              <SearchIcon sx={{ color: 'text.secondary', mr: { xs: 0.75, sm: 1 }, fontSize: { xs: 18, sm: 20 } }} />
              <InputBase
                placeholder="Search opinions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
              />
            </Paper>

            <Box sx={{ flexGrow: { xs: 0, sm: 1 } }} />

            {/* Filter Buttons */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, mr: { xs: 0.5, sm: 1 } }}>
              {['all', 'following', 'trending', 'recent'].map((f) => (
                <Button
                  key={f}
                  size="small"
                  variant={filter === f ? 'contained' : 'outlined'}
                  onClick={() => setFilter(f as any)}
                  disabled={f === 'following' && !currentUserId}
                  sx={{
                    textTransform: 'capitalize',
                    minWidth: 'auto',
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.375, sm: 0.5 },
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                  }}
                >
                  {f}
                </Button>
              ))}
            </Box>

            {/* Notifications */}
            <IconButton 
              size="small" 
              sx={{ 
                mr: { xs: 0.25, sm: 0.5 },
                minWidth: { xs: 36, sm: 40 },
                minHeight: { xs: 36, sm: 40 }
              }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon fontSize="small" sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </Badge>
            </IconButton>

            {/* User Menu */}
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              sx={{ 
                p: { xs: 0.375, sm: 0.5 },
                minWidth: { xs: 36, sm: 40 },
                minHeight: { xs: 36, sm: 40 }
              }}
            >
              <UserAvatar
                profileIcon={user?.profilePicture}
                username={currentUsername}
                size={36}
                sx={{ bgcolor: 'primary.main' }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => navigate('/profile')}>
                <PersonIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={() => navigate('/settings')}>
                <SettingsIcon sx={{ mr: 1 }} />
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
          {/* Header */}
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.75, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Active Opinions
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
              Discover and engage with the latest perspectives
            </Typography>
          </Box>

          {/* Mobile Filter */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mb: 2.5, overflowX: 'auto' }}>
            {['all', 'following', 'trending', 'recent'].map((f) => (
              <Chip
                key={f}
                label={f}
                onClick={() => setFilter(f as any)}
                color={filter === f ? 'primary' : 'default'}
                disabled={f === 'following' && !currentUserId}
                sx={{ textTransform: 'capitalize', fontSize: '0.875rem', height: 32 }}
              />
            ))}
          </Box>

          {/* Opinions Grid */}
          {loading && opinions.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress size={48} />
            </Box>
          ) : (
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              {filteredOpinions.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.125rem', mb: 1 }}>
                    No opinions found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                    {searchQuery ? 'Try a different search term' : 'Be the first to share an opinion!'}
                  </Typography>
                </Paper>
              ) : (
                <>
                  {filteredOpinions.map((opinion) => (
                    <OpinionCard
                      key={opinion._id}
                      opinion={opinion}
                      currentUserId={currentUserId}
                      onVote={handleVote}
                      voting={voting === opinion._id}
                    />
                  ))}
                  
                  {/* Infinite Scroll Trigger */}
                  <Box
                    ref={observerTarget}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 3,
                      minHeight: 60
                    }}
                  >
                    {loadingMore && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          Loading more opinions...
                        </Typography>
                      </Box>
                    )}
                    {!hasMore && opinions.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
                        You've reached the end of the feed
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </Container>
      </Box>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        message="Please log in to vote on opinions."
      />
    </MainLayout>
  );
};

export default Dashboard;
